import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Share2, MapPin, Clock, Home, MessageCircle } from "lucide-react";
import axios from "axios";
import SoulprintShareSystem from "@/components/SoulprintShareSystem";
import UniversalEnquiryModal from "@/components/UniversalEnquiryModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Session — guaranteed to exist regardless of entry point ──────────────────
// Hero.jsx also calls this on homepage load, so direct-to-quiz visitors
// who bypass the homepage will always have a valid session in localStorage.
function getOrCreateSession() {
  let id = localStorage.getItem("travelorza_session");
  if (!id) {
    id = `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("travelorza_session", id);
  }
  return id;
}

// Unified invite message — same across the entire platform
const INVITE_MESSAGE = `Hey, just figured out something crazy about myself.

Now I need your result too. 😄

Apparently we can compare our travel personalities and see how compatible we actually are.

Take the quiz:

https://travelorza.com/quiz

Send me your Soulprint when you're done. I'm genuinely curious what you get.`;


// ── ORZA Quiz Companion — bottom sheet, prominent post-Soulprint experience ──
// Result-page guided replies — invite-focused, never suggests retaking quiz (Addition 3)
const RESULT_GUIDED_REPLIES = [
  ["It resonates a lot", "Parts feel off", "Tell me more"],
  ["My approach to experiences", "How I connect with people", "Both"],
  ["Very different from me", "Similar in unexpected ways", "I'd love to find out"],
  ["Invite", "What does this mean for my next trip?"],
  ["Invite", "What should I explore next?"],
];

const RESULT_CONTINUATION_REPLIES = ["Invite", "Tell me more", "What should I explore next?"];

function parseResultResponse(rawText) {
  const match = rawText.match(/\[OPTIONS:\s*([^\]]+)\]/i);
  if (match) {
    const cleanText = rawText.replace(/\[OPTIONS:[^\]]+\]/i, "").trim();
    const options = match[1].split("|").map(s => s.trim()).filter(Boolean);
    return { text: cleanText, options };
  }
  return { text: rawText, options: [] };
}

function OrzaQuizCompanion({ result, sessionId, externalTrigger }) {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [resultGuidedPhase, setResultGuidedPhase] = useState(0);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  function buildOpener() {
    const firstName = (localStorage.getItem("travelorza_name") || "").split(" ")[0];
    const secondary = result.secondary_archetype;
    return `${firstName ? `${firstName}, ` : ""}seeing your ${result.archetype} profile — how does that land with you?${secondary ? ` That ${secondary} undercurrent is worth sitting with too.` : ""}`;
  }

  function openWithGreeting() {
    if (messages.length === 0) {
      setMessages([{ role: "assistant", content: buildOpener() }]);
      setQuickReplies(RESULT_GUIDED_REPLIES[0]);
    }
    setOpen(true);
    setMinimized(false);
    // Lock body scroll on mobile — same pattern as Homepage ORZA
    if (window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    }
  }

  function closeOrClose() {
    setMinimized(true);
    // Unlock body scroll on mobile
    document.body.style.overflow = "";
  }

  useEffect(() => {
    const t = setTimeout(openWithGreeting, 10000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (externalTrigger && !open) openWithGreeting();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTrigger]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const sendMsg = async (overrideMsg) => {
    const userMsg = (overrideMsg || input).trim();
    if (!userMsg || isLoading) return;
    setInput("");
    setQuickReplies([]);
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/concierge/chat`, {
        session_id: sessionId,
        message: userMsg,
        archetype: result.archetype,
        secondary_archetype: result.secondary_archetype || undefined,
        traveler_name: localStorage.getItem("travelorza_name") || undefined,
        dna_summary: localStorage.getItem("travelorza_dna_summary") || undefined,
      });
      const parsed = parseResultResponse(res.data.response);
      setMessages(prev => [...prev, { role: "assistant", content: parsed.text }]);

      const nextPhase = resultGuidedPhase + 1;
      setResultGuidedPhase(nextPhase);
      if (parsed.options.length > 0) {
        setQuickReplies(parsed.options);
      } else if (nextPhase < RESULT_GUIDED_REPLIES.length) {
        setQuickReplies(RESULT_GUIDED_REPLIES[nextPhase]);
      } else {
        setQuickReplies(RESULT_CONTINUATION_REPLIES);
      }
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "There was a connection issue — send that again and I'll respond." }]);
      setQuickReplies(RESULT_CONTINUATION_REPLIES);
    } finally { setIsLoading(false); }
  };

  if (!open) return null;

  if (minimized) {
    return (
      <button
        onClick={() => {
          setMinimized(false);
          if (window.innerWidth < 768) document.body.style.overflow = "hidden";
        }}
        className="fixed bottom-20 right-4 md:bottom-10 md:right-8 z-50 flex items-center gap-2 px-4 py-2.5 bg-[#C9A84C] text-black rounded-full shadow-lg text-xs font-semibold animate-fade-in"
      >
        <MessageCircle size={13} /> Continue with Orza
      </button>
    );
  }

  return (
    // Mobile: fixed full-screen overlay, opaque — same pattern as Homepage ORZA
    // Desktop: side panel bottom-right (unchanged)
    <div
      className="fixed z-50 animate-fade-in-up
                 inset-0
                 md:inset-auto md:bottom-10 md:left-auto md:right-8 md:max-w-md"
      style={{ animationFillMode: "forwards", touchAction: "none" }}
    >
      <div
        className="flex flex-col h-full
                   bg-[#050505]
                   md:bg-transparent md:glass md:rounded-2xl md:border md:border-[#C9A84C]/20 md:shadow-2xl md:h-[min(65vh,540px)]"
      >
        {/* Header — consistent Soulprint Companion branding (Ref 20) */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/35 flex items-center justify-center">
              <MessageCircle size={13} className="text-[#C9A84C]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#FAFAFA] tracking-wide">Orza</p>
              <p className="text-[9px] text-[#C9A84C]/60 tracking-[0.2em] uppercase">Soulprint Companion</p>
            </div>
          </div>
          <button onClick={closeOrClose} className="text-[#71717A] hover:text-white p-1 text-lg leading-none">&mdash;</button>
        </div>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-0">
          {messages.map((msg, i) => (
            <div key={i} className={`max-w-[88%] p-3.5 rounded-xl ${msg.role === "user" ? "ml-auto bg-white/5 border border-white/8" : "chat-message-ai pl-4"}`}>
              {msg.role === "assistant" && <p className="text-[8px] uppercase tracking-[0.2em] text-[#C9A84C] mb-1.5">Orza</p>}
              <p className={`text-sm leading-relaxed ${msg.role === "user" ? "text-[#FAFAFA]" : "text-[#D4D4D8]"}`}>{msg.content}</p>
            </div>
          ))}
          {isLoading && (
            <div className="chat-message-ai max-w-[88%] p-3.5 rounded-xl pl-4">
              <p className="text-[8px] uppercase tracking-[0.2em] text-[#C9A84C] mb-1.5">Orza</p>
              <div className="flex gap-1.5 items-center h-4">{[0,1,2].map(d => <span key={d} className="typing-dot w-1.5 h-1.5 rounded-full bg-[#C9A84C]/60 inline-block" />)}</div>
            </div>
          )}
          {/* Guided quick replies — button-first rule; invite chips open WhatsApp directly */}
          {quickReplies.length > 0 && !isLoading && (
            <div className="flex flex-wrap gap-2 pt-1">
              {quickReplies.map(r => {
                const isInvite = r === "Invite";
                const href = `https://wa.me/?text=${encodeURIComponent(INVITE_MESSAGE)}`;
                if (isInvite) {
                  return (
                    <a key={r} href={href} target="_blank" rel="noopener noreferrer"
                      className="text-xs px-3 py-1.5 border border-[#C9A84C]/30 text-[#C9A84C]/80 rounded-full hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] transition-all">
                      {r}
                    </a>
                  );
                }
                return (
                  <button key={r} onClick={() => sendMsg(r)}
                    className="text-xs px-3 py-1.5 border border-[#C9A84C]/30 text-[#C9A84C]/80 rounded-full hover:bg-[#C9A84C]/10 hover:text-[#C9A84C] transition-all">
                    {r}
                  </button>
                );
              })}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {/* Input — optional, not required */}
        <div className="px-5 py-4 border-t border-white/5 shrink-0">
          <div className="flex gap-2 items-end">
            <input type="text" value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMsg())}
              placeholder="Or type anything..."
              className="flex-1 bg-transparent border-b border-white/20 focus:border-[#C9A84C] outline-none text-sm text-white placeholder:text-white/25 pb-2 transition-colors"
            />
            <button onClick={() => sendMsg()} disabled={isLoading || !input.trim()}
              className="w-8 h-8 flex items-center justify-center bg-[#C9A84C] disabled:bg-[#C9A84C]/25 text-black disabled:text-black/30 rounded-sm">
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


const REVEAL_DELAYS = {
  ARCHETYPE:   600,
  DESCRIPTION: 1400,
  DNA:         2200,
  SHARE:       3200,
  MATTERS:     4000,
  DEEP_DIVE:   4800,
  JOURNEY:     5400,
  EXPERIENCES: 6000,
};
const TOTAL_QUESTIONS = 10;

// ── Archetype Config ──────────────────────────────────────────────────────────
const ARCHETYPE_CONFIG = {
  Explorer:       { color: "#E8A838", glow: "rgba(232,168,56,0.15)",  image: "https://images.unsplash.com/photo-1501854140801-50d01698950b?w=1400&q=80" },
  Romantic:       { color: "#E8738A", glow: "rgba(232,115,138,0.15)", image: "https://static.prod-images.emergentagent.com/jobs/951cafd7-d0c3-428a-884b-f00d92f5cb83/images/579df819394e67a807b0e29cb57896fa7d74a6896f385b69464ef110962b4a7e.png" },
  Celebrator:     { color: "#A855F7", glow: "rgba(168,85,247,0.15)",  image: "https://images.pexels.com/photos/35593189/pexels-photo-35593189.jpeg?auto=compress&cs=tinysrgb&w=1400" },
  Seeker:         { color: "#22C55E", glow: "rgba(34,197,94,0.15)",   image: "https://static.prod-images.emergentagent.com/jobs/951cafd7-d0c3-428a-884b-f00d92f5cb83/images/f4b1db47b17139414763c35dc51264fe7092429b2f57e752a88eaa1779a9c934.png" },
  Sensorialist:   { color: "#F97316", glow: "rgba(249,115,22,0.15)",  image: "https://static.prod-images.emergentagent.com/jobs/951cafd7-d0c3-428a-884b-f00d92f5cb83/images/06c97fccece45f84047a5e61ce5fe84252be5ec46da7d60089aab469dc54f4e5.png" },
  "Soul Traveler":{ color: "#06B6D4", glow: "rgba(6,182,212,0.15)",   image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1400&q=80" }
};

// ── Weighted Questions ────────────────────────────────────────────────────────
// Each option weights can assign points to MULTIPLE archetypes
const QUIZ_QUESTIONS = [
  {
    id: 1,
    category: "Motivation",
    question: "Before you book anything — what are you really chasing?",
    subtitle: "Answer from your gut, not your head.",
    options: [
      { text: "A story no one else can tell. Something I had to earn.", weights: { Explorer: 5, Seeker: 3 } },
      { text: "A feeling of complete connection — with someone or somewhere.", weights: { Romantic: 5, "Soul Traveler": 2 } },
      { text: "Celebration. To feel fully alive, surrounded by that energy.", weights: { Celebrator: 5, Sensorialist: 2 } },
      { text: "Escape from the noise. I need silence to find myself again.", weights: { Seeker: 5, "Soul Traveler": 3 } },
      { text: "Sensory richness. To taste, feel, and absorb a place completely.", weights: { Sensorialist: 5, Romantic: 1 } },
      { text: "Something I can't name — a pull toward meaning, ritual, transformation.", weights: { "Soul Traveler": 5, Seeker: 2 } }
    ]
  },
  {
    id: 2,
    category: "Scene",
    question: "Picture yourself at 9pm on the second night of your trip. You're...",
    subtitle: "Don't overthink it. Where does your mind go?",
    options: [
      { text: "Around a fire in the wilderness, plotting tomorrow's route.", weights: { Explorer: 5, Seeker: 2 } },
      { text: "At the most romantic table imaginable, time standing still.", weights: { Romantic: 5, Sensorialist: 2 } },
      { text: "In the middle of the dance floor, wondering why home exists.", weights: { Celebrator: 5, Sensorialist: 1 } },
      { text: "Sitting alone with a journal, the stars doing something to you.", weights: { Seeker: 5, "Soul Traveler": 3 } },
      { text: "Deep into a tasting menu that's rearranging your understanding of flavor.", weights: { Sensorialist: 5, Romantic: 2 } },
      { text: "In a candlelit ceremony with a stranger who feels like a teacher.", weights: { "Soul Traveler": 5, Seeker: 2 } }
    ]
  },
  {
    id: 3,
    category: "Decision",
    question: "What secretly drives your choice of destination?",
    subtitle: "Be honest with yourself here.",
    options: [
      { text: "Whether it's difficult to reach. The harder, the more interesting.", weights: { Explorer: 5, Seeker: 1 } },
      { text: "Whether it amplifies love. A perfect backdrop for us.", weights: { Romantic: 5, Sensorialist: 1 } },
      { text: "Whether something incredible is happening — a festival, a scene.", weights: { Celebrator: 5, Sensorialist: 2 } },
      { text: "Whether it has something ancient, unexplored, or rarely touched.", weights: { Seeker: 4, "Soul Traveler": 3, Explorer: 2 } },
      { text: "Whether the food, design, and culture are extraordinary.", weights: { Sensorialist: 5, Romantic: 1 } },
      { text: "Whether it's spiritually charged. Whether it holds something sacred.", weights: { "Soul Traveler": 5, Seeker: 2 } }
    ]
  },
  {
    id: 4,
    category: "Style",
    question: "Your travel style — in one sentence — would be:",
    subtitle: "This is how you move through the world.",
    options: [
      { text: "Figure it out when I get there. The plan is to have no plan.", weights: { Explorer: 5, Celebrator: 2 } },
      { text: "Slow, intentional, and deeply felt. Every moment chosen together.", weights: { Romantic: 5, Sensorialist: 2 } },
      { text: "Maximum experiences. Maximum people. Maximum energy.", weights: { Celebrator: 5, Sensorialist: 1 } },
      { text: "One place, deeply. Roots before wings. Slow travel always.", weights: { Seeker: 5, "Soul Traveler": 3 } },
      { text: "Curated down to every detail. I researched this for months.", weights: { Sensorialist: 5, Romantic: 2 } },
      { text: "Guided by meaning, not maps. I follow what feels sacred.", weights: { "Soul Traveler": 5, Seeker: 3 } }
    ]
  },
  {
    id: 5,
    category: "Body",
    question: "What do you want your body to feel while you're there?",
    subtitle: "Travel is physical as much as emotional.",
    options: [
      { text: "Pushed. Tested. Exhausted in the most satisfying way possible.", weights: { Explorer: 5, Seeker: 2 } },
      { text: "Soft. Warm. Held by something beautiful and unhurried.", weights: { Romantic: 5, Sensorialist: 2 } },
      { text: "Alive — pulse up, feet moving, every sense wide open.", weights: { Celebrator: 5, Sensorialist: 2 } },
      { text: "Quiet. Stripped of tension. Finally, genuinely at rest.", weights: { Seeker: 5, "Soul Traveler": 4 } },
      { text: "Indulged. Completely and unapologetically pampered.", weights: { Sensorialist: 5, Romantic: 2 } },
      { text: "Expanded. Like something in my chest broke open — in the best way.", weights: { "Soul Traveler": 5, Seeker: 2 } }
    ]
  },
  {
    id: 6,
    category: "Connection",
    question: "You meet a stranger on your trip and feel an instant connection. Why?",
    subtitle: "What draws people to you — and you to them — when you travel?",
    options: [
      { text: "They've done something I haven't yet — and they're going to tell me exactly how.", weights: { Explorer: 5, Seeker: 2 } },
      { text: "They remind you that love, in all its forms, is everywhere you look.", weights: { Romantic: 5, "Soul Traveler": 2 } },
      { text: "They know every hidden bar and exactly who to call to get in.", weights: { Celebrator: 5, Sensorialist: 2 } },
      { text: "They see through the surface of things. You could talk for days.", weights: { Seeker: 5, "Soul Traveler": 3 } },
      { text: "They know this place better than any guidebook — every taste, every secret.", weights: { Sensorialist: 5, Explorer: 1 } },
      { text: "They carry a kind of peace you want to understand and get closer to.", weights: { "Soul Traveler": 5, Seeker: 2 } }
    ]
  },
  {
    id: 7,
    category: "Centerpiece",
    question: "Which of these would be the centerpiece of your ideal trip?",
    subtitle: "The thing you'd tell everyone about when you got home.",
    options: [
      { text: "A climb, a dive, a trek, or an experience with real stakes.", weights: { Explorer: 5, Seeker: 1 } },
      { text: "The most intimate, romantic property you've ever stayed in.", weights: { Romantic: 5, Sensorialist: 2 } },
      { text: "Access to the most legendary music or cultural event of the year.", weights: { Celebrator: 5, Sensorialist: 1 } },
      { text: "A silent retreat or a journey deep into untouched wilderness.", weights: { Seeker: 5, "Soul Traveler": 3 } },
      { text: "A private tasting or an exclusive cultural immersion beyond all comparison.", weights: { Sensorialist: 5, Romantic: 1 } },
      { text: "A pilgrimage to a sacred site or a ceremony that shifts something in you.", weights: { "Soul Traveler": 5, Seeker: 2 } }
    ]
  },
  {
    id: 8,
    category: "Anti-preference",
    question: "The one thing that would quietly ruin a trip for you:",
    subtitle: "What you avoid reveals as much as what you seek.",
    options: [
      { text: "Being stuck in one place. Predictability. The feeling of cages.", weights: { Explorer: 5, Celebrator: 2 } },
      { text: "Being surrounded by strangers instead of the one person who matters.", weights: { Romantic: 5, Seeker: 1 } },
      { text: "Somewhere completely dead. No energy. No people. No pulse.", weights: { Celebrator: 5, Sensorialist: 2 } },
      { text: "Noise and crowds and constant surface-level interaction.", weights: { Seeker: 5, "Soul Traveler": 3 } },
      { text: "Poor quality. Mediocre food, generic spaces, zero curation.", weights: { Sensorialist: 5, Romantic: 2 } },
      { text: "Superficiality. Ticking boxes. Missing what a place actually means.", weights: { "Soul Traveler": 5, Seeker: 3 } }
    ]
  },
  {
    id: 9,
    category: "Sound",
    question: "The sound that should accompany your perfect journey:",
    subtitle: "Travel has a soundtrack. What's yours?",
    options: [
      { text: "Wind. Rain. The sound of my own heartbeat working hard.", weights: { Explorer: 5, Seeker: 2 } },
      { text: "Low music. A voice I love. The sound of a place being savored slowly.", weights: { Romantic: 5, Sensorialist: 2 } },
      { text: "Bass. Crowd energy. The electric frequency of thousands feeling it together.", weights: { Celebrator: 5, Sensorialist: 1 } },
      { text: "Silence. Or something close — a river, wind through grass, nothing else.", weights: { Seeker: 5, "Soul Traveler": 4 } },
      { text: "Clinking crystal. Market sounds. Music that belongs to this place only.", weights: { Sensorialist: 5, Romantic: 1 } },
      { text: "Chanting. Bells. The sound of something much older than this century.", weights: { "Soul Traveler": 5, Seeker: 2 } }
    ]
  },
  {
    id: 10,
    category: "Transformation",
    question: "When you imagine your most transformed self — what unlocked the change?",
    subtitle: "The last question. The most important one.",
    options: [
      { text: "Something I survived or conquered. A challenge that rewired my limits.", weights: { Explorer: 5, Seeker: 3 } },
      { text: "A love so present and undistracted it showed me what I'd been missing.", weights: { Romantic: 5, "Soul Traveler": 2 } },
      { text: "A moment of pure shared joy — when being alive felt like the only real religion.", weights: { Celebrator: 5, Sensorialist: 2 } },
      { text: "Silence. The kind that only comes when you stop running from yourself.", weights: { Seeker: 5, "Soul Traveler": 4 } },
      { text: "The slow realization that beauty — pursued without compromise — changes your brain.", weights: { Sensorialist: 5, Romantic: 2 } },
      { text: "Something I can't fully explain. A place. A ceremony. A realization in my bones.", weights: { "Soul Traveler": 5, Seeker: 2, Explorer: 1 } }
    ]
  }
];

// ── Score Calculation ─────────────────────────────────────────────────────────
function calcScores(answers) {
  const scores = { Explorer: 0, Romantic: 0, Celebrator: 0, Seeker: 0, Sensorialist: 0, "Soul Traveler": 0 };
  answers.forEach(a => {
    Object.entries(a.weights).forEach(([arch, w]) => {
      scores[arch] = (scores[arch] || 0) + w;
    });
  });
  return scores;
}

// ── Score Bar Component ───────────────────────────────────────────────────────
function ScoreBar({ archetype, score, total, isDominant, isSecondary }) {
  const { color } = ARCHETYPE_CONFIG[archetype] || { color: "#C9A84C" };
  const pct = total > 0 ? Math.round((score / total) * 100) : 0;
  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-2">
        <span className={`text-sm uppercase tracking-[0.15em] font-body ${isDominant ? "text-white font-semibold" : "text-[#71717A]"}`}>
          {archetype}
          {isDominant && <span className="ml-2 text-[9px] text-[#C9A84C] tracking-[0.2em]">PRIMARY</span>}
          {isSecondary && !isDominant && <span className="ml-2 text-[9px] text-[#A1A1AA] tracking-[0.2em]">SECONDARY</span>}
        </span>
        <span className={`text-sm font-medium`} style={{ color: isDominant ? color : "#71717A" }}>{pct}%</span>
      </div>
      <div className="h-1 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: isDominant ? color : `${color}60`, transition: "width 1.4s ease-out" }}
        />
      </div>
    </div>
  );
}

// ── Experience Card ───────────────────────────────────────────────────────────
function ExpCard({ exp, onInquire, archetypeColor }) {
  const CATEGORY_COLORS = {
    "Adrenaline & Exploration": "#E8A838", "Intimate Escapes": "#E8738A",
    "Elevated Indulgence": "#F97316", "Cultural Immersions": "#06B6D4",
    "Nature & Solitude": "#22C55E", "Rhythm & Celebration": "#A855F7"
  };
  const catColor = CATEGORY_COLORS[exp.category] || archetypeColor || "#C9A84C";
  return (
    <div data-testid={`result-exp-card-${exp._id}`} className="group rounded-xl overflow-hidden bg-[#111111] border border-white/5 hover:border-white/10 transition-all duration-300">
      <div className="h-48 overflow-hidden relative">
        <img
          src={exp.image_url}
          alt={exp.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={e => { e.target.src = "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&q=60"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
        {exp.duration && (
          <span className="absolute bottom-3 right-3 text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm bg-black/60 text-white/70 border border-white/10">
            {exp.duration}
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start gap-1 mb-1">
          <span className="text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm flex-shrink-0 mt-0.5" style={{ background: `${catColor}15`, color: catColor, border: `1px solid ${catColor}25` }}>
            {exp.category}
          </span>
        </div>
        <h4 className="font-heading text-xl font-light text-white mb-2 leading-snug mt-2">{exp.title}</h4>
        <div className="flex items-center gap-2 text-[#71717A] text-xs mb-4">
          <MapPin size={9} /><span>{exp.location}</span>
        </div>
        <button
          data-testid={`result-exp-inquire-${exp._id}`}
          onClick={() => onInquire && onInquire(exp)}
          className="w-full text-xs py-2.5 border rounded-sm tracking-[0.2em] uppercase transition-all duration-200 hover:bg-opacity-10"
          style={{ borderColor: `${catColor}40`, color: catColor, background: `${catColor}06` }}
        >
          Design My Journey
        </button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Quiz() {
  // ── Persistence: initialize from localStorage if a Soulprint already exists ─
  // Runs once on mount. If a full result was previously stored, skip to the
  // result page immediately so returning users never see the quiz intro.
  const savedResultJson = typeof window !== "undefined"
    ? localStorage.getItem("travelorza_result")
    : null;
  const savedResult = (() => {
    try { return savedResultJson ? JSON.parse(savedResultJson) : null; } catch { return null; }
  })();

  const [phase, setPhase] = useState(() =>
    savedResult?.archetype ? "result" : "intro"
  );
  const [orzaExternalTrigger, setOrzaExternalTrigger] = useState(false);
  const shareCardRef = useRef(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(savedResult?.archetype ? savedResult : null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // If returning to existing result, show all sections immediately (skip animation)
  const [revealStep, setRevealStep] = useState(savedResult?.archetype ? 8 : 0);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "" });
  const [leadSaved, setLeadSaved] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [emailDelivery, setEmailDelivery] = useState({ email: "", sent: false, loading: false });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalExp, setModalExp] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const revealTimers = useRef([]);

  // Gate state
  const [gateCompleted, setGateCompleted] = useState(false);
  const [gateForm, setGateForm] = useState({ name: "", phone: "", email: "", travel_companion: "" });
  const [gateLoading, setGateLoading] = useState(false);
  const [gateError, setGateError] = useState("");

  // Session — created here if user navigated directly to /quiz (bypassing homepage)
  const [sessionId] = useState(getOrCreateSession);

  const q = QUIZ_QUESTIONS[currentQ];
  const progress = phase === "questions" ? ((currentQ + 1) / QUIZ_QUESTIONS.length) * 100 : 0;

  // Progressive reveal on result screen
  useEffect(() => {
    if (phase === "result" && result) {
      revealTimers.current = [
        setTimeout(() => setRevealStep(1), REVEAL_DELAYS.ARCHETYPE),
        setTimeout(() => setRevealStep(2), REVEAL_DELAYS.DESCRIPTION),
        setTimeout(() => setRevealStep(3), REVEAL_DELAYS.DNA),
        setTimeout(() => setRevealStep(4), REVEAL_DELAYS.SHARE),
        setTimeout(() => setRevealStep(5), REVEAL_DELAYS.MATTERS),
        setTimeout(() => setRevealStep(6), REVEAL_DELAYS.DEEP_DIVE),
        setTimeout(() => setRevealStep(7), REVEAL_DELAYS.JOURNEY),
        setTimeout(() => setRevealStep(8), REVEAL_DELAYS.EXPERIENCES),
      ];
    }
    return () => revealTimers.current.forEach(clearTimeout);
  }, [phase, result]);

  // IntersectionObserver — trigger ORZA when user scrolls to share card section
  // IntersectionObserver, setOrzaExternalTrigger are stable globals/setters — phase+result are the reactive deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!shareCardRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setOrzaExternalTrigger(true); },
      { threshold: 0.3 }
    );
    observer.observe(shareCardRef.current);
    return () => observer.disconnect();
  }, [phase, result]);

  const handleSelect = (option) => { if (!isTransitioning) setSelected(option); };

  const handleNext = async () => {
    if (!selected || isTransitioning) return;
    setIsTransitioning(true);

    const newAnswers = [...answers, {
      question_id: q.id,
      weights: selected.weights,
      archetype: Object.entries(selected.weights).sort((a, b) => b[1] - a[1])[0]?.[0] || "",
      selected_option: q.options.indexOf(selected)
    }];
    setAnswers(newAnswers);

    await new Promise(r => setTimeout(r, 350));

    // After Q3 (index 2), show soft lead gate if not already completed
    if (currentQ === 2 && !gateCompleted) {
      setSelected(null);
      setIsTransitioning(false);
      setPhase("gate");
      return;
    }

    if (currentQ === QUIZ_QUESTIONS.length - 1) {
      setPhase("calculating");
      try {
        const res = await axios.post(`${API}/quiz/submit`, { answers: newAnswers, session_id: sessionId });
        const data = res.data;
        // Store Soulprint data in localStorage for ORZA personalization
        localStorage.setItem("travelorza_archetype", data.archetype);
        localStorage.setItem("travelorza_result_id", data.result_id);
        if (data.secondary_archetype) localStorage.setItem("travelorza_secondary_archetype", data.secondary_archetype);
        if (data.scores) {
          const total = Object.values(data.scores).reduce((a, b) => a + b, 0) || 1;
          const top3 = Object.entries(data.scores).sort((a, b) => b[1] - a[1]).slice(0, 3)
            .map(([arch, score]) => `${arch} ${Math.round((score / total) * 100)}%`).join(" | ");
          localStorage.setItem("travelorza_dna_summary", top3);
        }
        // Store full result for persistence — returning users skip straight to results page
        localStorage.setItem("travelorza_result", JSON.stringify(data));
        await new Promise(r => setTimeout(r, 1800));
        setResult(data);
        setPhase("result");
        setRevealStep(0);
      } catch {
        const scores = calcScores(newAnswers);
        const dominant = Object.entries(scores).sort((a, b) => b[1] - a[1])[0][0];
        const secondary = Object.entries(scores).sort((a, b) => b[1] - a[1])[1][0];
        const fallbackResult = {
          archetype: dominant, secondary_archetype: secondary,
          scores, matched_experiences: [],
          tagline: { Explorer: "The Boundless Wanderer", Romantic: "The Eternal Lover", Celebrator: "The Joy Architect", Seeker: "The Deep Diver", Sensorialist: "The Taste of the World", "Soul Traveler": "The Sacred Wanderer" }[dominant],
          description: "Your Soulprint has been revealed. Talk to your Experience Architect to discover the journeys designed for who you are.",
          color: ARCHETYPE_CONFIG[dominant]?.color || "#C9A84C"
        };
        setResult(fallbackResult);
        localStorage.setItem("travelorza_archetype", dominant);
        localStorage.setItem("travelorza_result", JSON.stringify(fallbackResult));
        await new Promise(r => setTimeout(r, 1800));
        setPhase("result");
        setRevealStep(0);
      }
    } else {
      setCurrentQ(prev => prev + 1);
      setSelected(null);
      setIsTransitioning(false);
    }
  };

  const handleBack = () => {
    if (currentQ === 0) { setPhase("intro"); setAnswers([]); }
    else { setCurrentQ(prev => prev - 1); setAnswers(prev => prev.slice(0, -1)); }
    setSelected(null);
  };

  const handleGateContinue = async () => {
    if (!gateForm.name || !gateForm.phone) { setGateError("Name and WhatsApp number are required."); return; }
    setGateLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: gateForm.name, phone: gateForm.phone, email: gateForm.email || "",
        travel_companion: gateForm.travel_companion, source: "quiz_gate",
        session_id: sessionId,   // links gate lead to quiz session
        message: `Mid-quiz capture at Q3. Companion: ${gateForm.travel_companion}`
      }, { timeout: 8000 });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[Quiz] Gate lead capture failed (non-blocking):", err);
    } finally {
      setGateLoading(false);
      setGateCompleted(true);
      // Store name for ORZA personalization
      if (gateForm.name) localStorage.setItem("travelorza_name", gateForm.name);
      setPhase("questions");
      setCurrentQ(3);
    }
  };

  const handleGateSkip = () => {
    setGateCompleted(true);
    setPhase("questions");
    setCurrentQ(3);
  };

  const handleSaveLead = async () => {
    if (!leadForm.name || !leadForm.email) return;
    setLeadLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: leadForm.name, email: leadForm.email, phone: leadForm.phone || "",
        archetype: result.archetype,
        preferred_experience: result.matched_experiences?.[0]?.title || "",
        message: `Quiz result: ${result.archetype} (${result.secondary_archetype ? `secondary: ${result.secondary_archetype}` : ""}). Scores: ${JSON.stringify(result.scores)}`
      }, { timeout: 8000 });
      setLeadSaved(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("[Quiz] Lead save failed (non-blocking):", err);
    } finally { setLeadLoading(false); }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/quiz`;
    if (navigator.share) {
      try { await navigator.share({ title: "Discover Your Soulprint", text: "Find out how you truly travel.", url }); } catch {} // eslint-disable-line no-empty
    } else {
      navigator.clipboard?.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    }
  };

  const handleResultShare = () => {
    const shareText = `Hey,\n\nI just figured out something surprisingly accurate about myself.\n\nI discovered my Soulprint and it completely changed the way I think about travel, growth and why certain experiences have always felt more meaningful than others.\n\nApparently my dominant archetype right now is:\n\n${result?.archetype}\n\nwith a secondary influence of:\n\n${result?.secondary_archetype || "—"}\n\nWhat fascinated me most is that Soulprint isn't a personality label.\n\nIt reveals what you are currently seeking in life and how you need to experience the world in order to grow, transform and evolve.\n\nNow I'm curious about yours.\n\nTake the discovery and let's compare our Soulprints:\n\nhttps://travelorza.com/quiz\n\nI have a feeling the result might explain a lot more than you expect.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  const handleCopyLink = () => {
    navigator.clipboard?.writeText("https://travelorza.com/quiz");
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const cfg = result ? (ARCHETYPE_CONFIG[result.archetype] || ARCHETYPE_CONFIG.Explorer) : null;
  const secondaryCfg = result?.secondary_archetype ? (ARCHETYPE_CONFIG[result.secondary_archetype] || {}) : {};
  const totalScore = result ? Object.values(result.scores || {}).reduce((a, b) => a + b, 0) : 0;
  const sortedScores = result ? Object.entries(result.scores || {}).sort((a, b) => b[1] - a[1]) : [];

  // ── INTRO SCREEN ─────────────────────────────────────────────────────────────
  if (phase === "intro") return (
    <div data-testid="quiz-intro" className="min-h-screen bg-[#0A0A0A] relative flex flex-col items-center justify-center px-6 text-center overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://static.prod-images.emergentagent.com/jobs/951cafd7-d0c3-428a-884b-f00d92f5cb83/images/3f59c767f92dd354bb720e7d1d706f3dc8ee060da8237bc2057b25772087b368.png" alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/50 to-[#0A0A0A]" />
      </div>
      <div className="relative z-10 max-w-2xl animate-fade-in-up">
        <Link to="/" className="inline-flex items-center gap-2 text-[#71717A] hover:text-[#C9A84C] text-xs uppercase tracking-[0.2em] mb-14 transition-colors">
          <ArrowLeft size={12} />Back
        </Link>
        <p className="overline mb-6">Soulprint Discovery</p>
        <h1 className="font-heading font-light text-white tracking-tight leading-tight mb-5 text-center" style={{ fontSize: "clamp(1.4rem, 4.2vw, 4.5rem)" }}>
          <span className="block whitespace-nowrap">Every journey begins with</span>
          <span className="block italic text-[#C9A84C]">understanding you.</span>
        </h1>
        <p className="text-[#A1A1AA] text-base leading-relaxed mb-4 max-w-lg mx-auto">
          A short, thoughtfully designed assessment that reveals how you truly experience travel.
        </p>
        <p className="font-heading text-lg font-light italic text-[#D4C49A] mb-10 tracking-wide">
          &ldquo;Because the right journey isn&apos;t chosen. It&apos;s aligned.&rdquo;
        </p>

        {/* Archetype preview */}
        <div className="flex justify-center gap-2 mb-10 flex-wrap">
          {Object.entries(ARCHETYPE_CONFIG).map(([arch, { color }]) => (
            <span key={arch} className="text-[9px] uppercase tracking-[0.15em] px-3 py-1 rounded-sm border" style={{ color, borderColor: `${color}30`, background: `${color}10` }}>
              {arch}
            </span>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
          <button data-testid="quiz-start-btn" onClick={() => setPhase("questions")} className="btn-gold text-xs px-14 py-4">
            Discover Your Soulprint
          </button>
          <button
            data-testid="quiz-share-btn"
            onClick={handleShare}
            className="btn-outline-gold text-xs px-8 py-4 inline-flex items-center justify-center gap-2"
          >
            <Share2 size={13} />
            {linkCopied ? "Link Copied!" : "Share with a Friend"}
          </button>
        </div>
        <p className="text-[#71717A] text-xs uppercase tracking-[0.15em]">3 minutes · 10 questions · Instant archetype reveal</p>
      </div>
    </div>
  );

  // ── GATE SCREEN (After Q3) ────────────────────────────────────────────────────
  if (phase === "gate") return (
    <div data-testid="quiz-gate" className="min-h-screen bg-[#0A0A0A] flex flex-col">
      {/* Progress bar at 35% */}
      <div className="fixed top-0 left-0 right-0 z-50 h-0.5 bg-white/5">
        <div className="h-full bg-[#C9A84C]" style={{ width: "35%" }} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 max-w-xl mx-auto w-full">
        <div className="w-full mb-8">
          <button
            data-testid="gate-back-btn"
            onClick={() => { setPhase("questions"); setCurrentQ(2); }}
            className="text-[#71717A] hover:text-[#C9A84C] transition-colors text-xs uppercase tracking-[0.2em] flex items-center gap-2 mb-10"
          >
            <ArrowLeft size={12} />Back
          </button>

          <p className="overline mb-4">03 of 10 Complete</p>
          <h2 className="font-heading text-4xl md:text-5xl font-light text-white tracking-tight mb-4">
            Before we continue
          </h2>
          <p className="text-[#A1A1AA] text-sm leading-relaxed">
            Share your details and we&apos;ll send your personalized Soulprint results and curated journey recommendations directly to you.
          </p>
        </div>

        <div className="w-full space-y-5">
          {/* Travel companion question */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#A1A1AA] mb-3">Who are you planning to travel with?</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {["Solo", "Couple", "Friends", "Family"].map(opt => (
                <button
                  key={opt}
                  type="button"
                  data-testid={`gate-companion-${opt.toLowerCase()}`}
                  onClick={() => setGateForm(f => ({ ...f, travel_companion: f.travel_companion === opt ? "" : opt }))}
                  className={`py-3 text-sm rounded-xl border transition-all duration-200 ${
                    gateForm.travel_companion === opt
                      ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]"
                      : "border-white/10 bg-[#111111] text-[#A1A1AA] hover:border-white/25 hover:text-white"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Full Name <span className="text-[#C9A84C]">*</span></label>
            <input
              data-testid="gate-name"
              type="text" value={gateForm.name}
              onChange={e => setGateForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Your full name"
              className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] mb-1.5">WhatsApp Number <span className="text-[#C9A84C]">*</span></label>
            <input
              data-testid="gate-phone"
              type="tel" value={gateForm.phone}
              onChange={e => setGateForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="+91 XXXXX XXXXX"
              className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Email <span className="text-[#71717A] text-[9px] normal-case tracking-normal">(optional — for your full report)</span></label>
            <input
              data-testid="gate-email"
              type="email" value={gateForm.email}
              onChange={e => setGateForm(f => ({ ...f, email: e.target.value }))}
              placeholder="your@email.com"
              className="w-full bg-transparent border-b border-white/15 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/20 py-2 text-sm transition-colors"
            />
          </div>

          {gateError && (
            <p data-testid="gate-error" className="text-red-400 text-xs">{gateError}</p>
          )}

          <button
            data-testid="gate-continue-btn"
            onClick={handleGateContinue}
            disabled={gateLoading}
            className="w-full btn-gold py-4 text-xs disabled:opacity-40 flex items-center justify-center gap-2 mt-2"
          >
            {gateLoading ? "Saving..." : <>Continue My Journey <ArrowRight size={13} /></>}
          </button>

          <button
            data-testid="gate-skip-btn"
            onClick={handleGateSkip}
            className="w-full text-center text-[#71717A] text-xs uppercase tracking-[0.2em] hover:text-[#C9A84C] transition-colors py-2"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );

  // ── CALCULATING SCREEN ────────────────────────────────────────────────────────
  if (phase === "calculating") return (
    <div data-testid="quiz-calculating" className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center">
      <div className="text-center animate-fade-in">
        <div className="relative w-20 h-20 mx-auto mb-8">
          <div className="absolute inset-0 border-2 border-[#C9A84C]/20 rounded-full" />
          <div className="absolute inset-0 border-2 border-transparent border-t-[#C9A84C] rounded-full animate-spin" />
          <div className="absolute inset-3 border border-[#C9A84C]/10 rounded-full" />
        </div>
        <p className="overline mb-4">Processing Your Soulprint</p>
        <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-3">Mapping your answers...</h2>
        <p className="text-[#71717A] text-sm max-w-xs mx-auto leading-relaxed">
          We&apos;re analyzing your responses across all six archetypes to find your dominant travel identity.
        </p>
        <div className="flex justify-center gap-6 mt-8">
          {Object.entries(ARCHETYPE_CONFIG).map(([arch, { color }], i) => (
            <div key={arch} className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: color, animationDelay: `${i * 0.13}s` }} />
          ))}
        </div>
      </div>
    </div>
  );

  // ── RESULT SCREEN ─────────────────────────────────────────────────────────────
  if (phase === "result" && result) {
    // Presentation-only helpers (no logic changes)
    const descFragments = result.description
      .split(/(?<=[.!])\s+/)
      .filter(s => s.trim().length > 4);

    const interpretationText = sortedScores.length >= 2
      ? `Primarily ${sortedScores[0][0]} with strong ${sortedScores[1][0]}${sortedScores[2] ? ` and ${sortedScores[2][0]}` : ""} influences.`
      : `Your primary Soulprint is ${sortedScores[0]?.[0] || result.archetype}.`;

    // Frontend-only context map (uses existing archetype philosophies, presentation only)
    const WHY_MATTERS = {
      Explorer: {
        headline: "You travel to discover what's beyond the map.",
        body: "Most people travel to destinations. You travel to states of being. The challenge, the unknown, the edge — these aren't side effects of your journeys. They are the point.",
        pull: "The right journey for you isn't a package. It's a challenge."
      },
      Romantic: {
        headline: "You travel to feel more deeply.",
        body: "For you, the most memorable travel moments aren't places — they're moments of pure, undistracted presence. Connection is your compass. Stillness is your luxury.",
        pull: "The right journey doesn't scale for groups. It's designed for two."
      },
      Celebrator: {
        headline: "You travel to be fully, unapologetically alive.",
        body: "Energy matters to you. The pulse of a place, the people you find there, the nights that become mornings — these are the memories that define you.",
        pull: "The right journey has a frequency. It feels like life at full volume."
      },
      Seeker: {
        headline: "You travel to understand what can't be found anywhere online.",
        body: "Depth is your compass. The ordinary path rarely leads where you need to go. Ancient, untouched, silent — these words mean something to you that most people simply don't feel.",
        pull: "The right journey strips away noise. It brings you closer to what actually matters."
      },
      Sensorialist: {
        headline: "You travel through every one of your senses.",
        body: "A single meal. A texture. A fragrance that stays with you for years. These are your deepest travel memories — and they are worth designing deliberately.",
        pull: "The right journey doesn't cut corners. Every detail is a deliberate act of excellence."
      },
      "Soul Traveler": {
        headline: "You travel to return to yourself.",
        body: "The places that move you most are the ones that feel sacred — not always by design, but by feeling. You seek transformation, not just experience.",
        pull: "The right journey doesn't just take you somewhere. It changes who you are when you leave."
      }
    };
    const matters = WHY_MATTERS[result.archetype] || {
      headline: result.tagline || "",
      body: result.description || "",
      pull: ""
    };

    return (
      <div data-testid="quiz-result" className="min-h-screen bg-[#0A0A0A] overflow-x-hidden">

        {/* ════════════════════════════════════════════════════════════
            SECTION 1 — CINEMATIC SOULPRINT REVEAL
            ════════════════════════════════════════════════════════════ */}
        <div className="relative h-screen flex flex-col justify-end overflow-hidden">
          <img
            src={cfg.image}
            alt={result.archetype}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ transition: "opacity 1.2s ease" }}
          />
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(135deg, ${cfg.glow} 0%, transparent 55%), linear-gradient(to top, #0A0A0A 0%, rgba(10,10,10,0.6) 45%, rgba(10,10,10,0.05) 100%)` }}
          />

          {/* Home */}
          <Link
            to="/"
            data-testid="quiz-result-home-btn"
            className="absolute top-6 left-6 z-20 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/55 hover:text-[#C9A84C] transition-colors glass px-4 py-2 rounded-lg border border-white/8"
          >
            <Home size={11} />Home
          </Link>

          {/* Hero content: mobile = flex-col justify-end (evolution above archetype at bottom)
                         desktop = absolute-positioned evolution upper-right + archetype bottom-left */}
          <div className="relative z-10 h-full flex flex-col justify-end px-8 md:px-16 pb-14 md:pb-20 pt-20 md:pt-16 max-w-6xl">

            {/* EVOLUTION COPY — desktop: absolute upper-right quad, mobile: inline above archetype */}

            {/* Desktop only — large block, upper-right, high contrast */}
            <div className={`hidden md:block absolute top-[18%] right-0 md:right-16 max-w-[480px] text-right transition-all duration-1000 ${revealStep >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              style={{ filter: "drop-shadow(0 2px 24px rgba(0,0,0,0.95))" }}>
              <p
                className="font-heading font-light italic text-white leading-snug mb-3"
                style={{ fontSize: "clamp(1.75rem, 2.6vw, 2.25rem)", lineHeight: 1.35, color: "rgba(255,255,255,0.95)", textShadow: "0 2px 16px rgba(0,0,0,0.95), 0 1px 4px rgba(0,0,0,1)" }}
              >
                This is the version of yourself<br />
                that is asking to be experienced<br />
                right now.
              </p>
              <p
                className="font-heading font-light italic"
                style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.3rem)", lineHeight: 1.45, color: "rgba(255,255,255,0.78)", textShadow: "0 1px 12px rgba(0,0,0,0.9)" }}
              >
                It wasn&apos;t always this way.
              </p>
              <p
                className="font-heading font-light italic"
                style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.3rem)", lineHeight: 1.45, color: "rgba(255,255,255,0.78)", textShadow: "0 1px 12px rgba(0,0,0,0.9)" }}
              >
                And it probably won&apos;t stay<br />this way forever.
              </p>
            </div>

            {/* Archetype name block — bottom left + mobile evolution copy directly above it */}
            <div>
              {/* Mobile only — evolution copy sits directly above archetype title */}
              <div className={`md:hidden mb-7 transition-all duration-700 ${revealStep >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <p
                  className="font-heading font-light italic text-white leading-snug mb-2"
                  style={{ fontSize: "clamp(1.3rem, 5.5vw, 1.65rem)", lineHeight: 1.35, color: "rgba(255,255,255,0.95)", textShadow: "0 2px 12px rgba(0,0,0,0.95)" }}
                >
                  This is the version of yourself<br />
                  that is asking to be experienced<br />
                  right now.
                </p>
                <p
                  className="font-heading font-light italic"
                  style={{ fontSize: "clamp(0.875rem, 3.5vw, 1.05rem)", lineHeight: 1.45, color: "rgba(255,255,255,0.72)", textShadow: "0 1px 8px rgba(0,0,0,0.9)" }}
                >
                  It wasn&apos;t always this way. And it probably won&apos;t stay this way forever.
                </p>
              </div>

              {/* Archetype label */}
              <div className={`transition-all duration-700 mb-5 ${revealStep >= 0 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <p className="text-[9px] md:text-[10px] uppercase tracking-[0.5em] font-semibold" style={{ color: `${cfg.color}75` }}>
                  Your Travel Soulprint
                </p>
              </div>
              <div className={`transition-all duration-700 delay-200 ${revealStep >= 0 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
                <div className="flex items-baseline gap-3 md:gap-5 mb-3 flex-wrap">
                  <span className="font-heading font-light" style={{ fontSize: "clamp(1.8rem, 3.5vw, 3.5rem)", color: `${cfg.color}55` }}>THE</span>
                  <h1 data-testid="quiz-result-archetype" className="font-heading font-light tracking-tight leading-none" style={{ fontSize: "clamp(3.5rem, 11vw, 8rem)", color: cfg.color }}>
                    {result.archetype.toUpperCase()}
                  </h1>
                </div>
              </div>
              <div className={`transition-all duration-700 delay-400 ${revealStep >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                <p className="font-heading font-light italic text-white/65" style={{ fontSize: "clamp(1.1rem, 2.5vw, 1.75rem)" }}>
                  {result.tagline}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT AREA — New section order: Description → DNA → Share → Why Matters → Deep Dive → Journey → Experiences */}
        <div className="max-w-4xl mx-auto px-6 md:px-12">

          {/* DESCRIPTION — Emotional fragments + Soulprint Blend */}
          <section className={`py-20 md:py-28 text-center border-b border-white/5 transition-all duration-700 ${revealStep >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="max-w-2xl mx-auto mb-10 space-y-3 md:space-y-4">
              {descFragments.map((fragment, i) => (
                <p key={`df-${i}`} className="font-heading font-light text-white/88 leading-snug" style={{ fontSize: "clamp(1.1rem, 2.6vw, 1.5rem)" }}>{fragment}</p>
              ))}
            </div>
            {result.secondary_archetype && (
              <div className="mt-14 text-center">
                <p className="text-[9px] uppercase tracking-[0.4em] text-[#71717A] mb-5">Soulprint Blend</p>
                <div className="inline-block px-10 py-7 rounded-sm border" style={{ borderColor: `${secondaryCfg.color}35`, background: `${secondaryCfg.color}06` }}>
                  <p className="font-heading font-light mb-1" style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", color: secondaryCfg.color }}>{result.secondary_archetype}</p>
                  {result.secondary_tagline && <p className="text-[#71717A] text-xs tracking-widest uppercase mb-3">&middot; {result.secondary_tagline}</p>}
                  <p className="text-white/55 text-sm italic">You blend the best with {result.secondary_archetype}s</p>
                </div>
              </div>
            )}
          </section>

          {/* SECTION 2 — TRAVEL DNA */}
          <section className={`py-20 md:py-28 border-b border-white/5 transition-all duration-700 ${revealStep >= 3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-14">
              <p className="overline mb-4">Your Travel DNA</p>
              <h2 className="font-heading font-light text-white mb-6 tracking-tight leading-tight" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
                How Your Soulprint<br /><span className="italic" style={{ color: cfg.color }}>Breaks Down</span>
              </h2>
              <p className="text-[#71717A] text-sm leading-relaxed max-w-md mx-auto">
                Your Soulprint is not singular. It is a blend of multiple travel personalities &mdash; each shaping how you relate to the world when you move through it.
              </p>
            </div>
            <div className="max-w-xl mx-auto">
              {sortedScores.map(([arch, score]) => (
                <ScoreBar key={arch} archetype={arch} score={score} total={totalScore} isDominant={arch === result.archetype} isSecondary={arch === result.secondary_archetype} />
              ))}
              <div className="mt-8 px-5 py-4 rounded-sm border text-sm leading-relaxed text-center" style={{ borderColor: `${cfg.color}18`, background: `${cfg.color}05`, color: `${cfg.color}85` }}>
                {interpretationText}
              </div>
            </div>
          </section>

          {/* SECTION 3 — SHARE SOULPRINT (Issues 3, 4, 5, 6 — correct order) */}
          <section ref={shareCardRef} className={`py-20 md:py-24 border-b border-white/5 transition-all duration-700 ${revealStep >= 4 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="overline mb-6">Share Soulprint</p>

            {/* Issue 2 — SHARE MY SOULPRINT: primary WhatsApp CTA (most dominant) */}
            <div className="mb-12 max-w-sm">
              <button
                data-testid="result-share-whatsapp-btn"
                onClick={handleResultShare}
                className="w-full btn-gold py-4 text-sm tracking-[0.22em] uppercase mb-3"
              >
                Share My Soulprint
              </button>
              <button
                data-testid="result-copy-link-btn"
                onClick={handleCopyLink}
                className="w-full py-2.5 border border-white/12 text-[#A1A1AA] rounded-sm text-xs hover:border-[#C9A84C]/28 hover:text-[#C9A84C] transition-colors flex items-center justify-center gap-2"
              >
                <Share2 size={11} />{linkCopied ? "Link Copied!" : "Copy Link"}
              </button>
            </div>

            {/* Issue 4 — Both cards always visible simultaneously */}
            <SoulprintShareSystem archetype={result.archetype} />

            {/* Issue 5 — Supporting copy appears AFTER cards */}
            <div className="mt-14 max-w-2xl">
              <h2 className="font-heading text-2xl md:text-3xl font-light text-white mb-6 tracking-tight leading-tight">
                Sometimes the people closest to us are not looking for the same things we are.<br />
                <span className="italic" style={{ color: cfg.color }}>That&apos;s exactly what makes understanding them valuable.</span>
              </h2>
              <div className="text-[#A1A1AA] text-sm leading-relaxed space-y-4 mb-8">
                <p>You now know what your Soulprint reveals about who you need to be, experience and explore in this phase of your life. But what about the people closest to you?</p>
                <div className="pl-4 border-l border-white/10 space-y-1 text-[#71717A]">
                  <p>Your partner.</p><p>Your best friend.</p><p>Your family.</p><p>Your travel companions.</p>
                </div>
                <p>What archetype is currently guiding them? What experiences are they truly seeking? What part of themselves is waiting to be explored?</p>
                <p>Invite them to discover their Soulprint and compare how similar or different your current journeys really are.</p>
              </div>
              {/* Invite CTA — unified message, simplified label */}
              <a
                data-testid="result-invite-friend-btn"
                href={`https://wa.me/?text=${encodeURIComponent(INVITE_MESSAGE)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 btn-outline-gold px-8 py-3 text-xs"
              >
                Invite
              </a>
            </div>
          </section>

          {/* SECTION 4 — WHY THIS SOULPRINT MATTERS (Evolution Theory) */}
          <section className={`py-20 md:py-24 border-b border-white/5 transition-all duration-700 ${revealStep >= 5 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="overline mb-4">Why This Soulprint Matters</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-10 tracking-tight max-w-2xl leading-tight">
              Your Soulprint is not a fixed personality label.
            </h2>
            <div className="max-w-2xl space-y-6 text-[#A1A1AA] text-base leading-relaxed">
              <p>It is a reflection of what you are seeking most strongly in this phase of your life.</p>
              <p>A few years ago, you might have answered these questions very differently.</p>
              <div className="pl-5 border-l-2 space-y-1 text-[#71717A] text-sm" style={{ borderColor: `${cfg.color}30` }}>
                <p>When you were a student.</p><p>When you fell in love.</p><p>When you got your first salary.</p>
                <p>When you received a promotion.</p><p>When life challenged you.</p><p>Or when life changed you.</p>
              </div>
              <p>The archetype that feels dominant today may not be the archetype that guides you tomorrow.</p>
              <p style={{ color: cfg.color + "CC" }}>That isn&apos;t inconsistency. It&apos;s growth.</p>
              <p>Every archetype exists within all of us. At different points in life, different parts of us step forward. Different phases of life call different archetypes forward. Life continuously asks different things from us. And we respond by growing into different versions of ourselves.</p>
              <p>Your Soulprint reveals what your current chapter is asking you to explore.</p>
              <p>Retaking the discovery over time may reveal a different archetype becoming dominant. Not because the system changed. Because you did.</p>
            </div>
            <div className="mt-10 pt-8 border-t border-white/5 max-w-xl">
              <p className="font-heading text-lg font-light italic leading-relaxed" style={{ color: `${cfg.color}85` }}>
                &ldquo;The question isn&apos;t whether your Soulprint will change. The question is who you will become when it does.&rdquo;
              </p>
            </div>
          </section>

          {/* SECTION 5 — ARCHETYPE DEEP DIVE */}
          <section className={`py-20 md:py-24 border-b border-white/5 transition-all duration-700 ${revealStep >= 6 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="overline mb-4">Archetype Deep Dive</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-10 tracking-tight max-w-xl leading-tight">{matters.headline}</h2>
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              <div className="md:col-span-2">
                <p className="text-[#A1A1AA] text-base leading-relaxed mb-8">{matters.body}</p>
                {result.ideal_experiences?.length > 0 && (
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.28em] text-[#71717A] mb-4">Experiences Aligned With Your Soulprint</p>
                    <div className="flex flex-wrap gap-2">
                      {result.ideal_experiences.map((exp) => (
                        <span key={exp} className="text-xs px-3 py-1.5 rounded-sm border" style={{ borderColor: `${cfg.color}28`, color: `${cfg.color}85`, background: `${cfg.color}05` }}>{exp}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {matters.pull && (
                <div className="flex items-start md:items-center">
                  <div className="rounded-xl p-6 md:p-7 w-full" style={{ background: `${cfg.color}06`, border: `1px solid ${cfg.color}18` }}>
                    <div className="w-7 h-px mb-5" style={{ background: cfg.color }} />
                    <p className="font-heading text-lg font-light italic leading-relaxed" style={{ color: `${cfg.color}95` }}>&ldquo;{matters.pull}&rdquo;</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* SECTION 5.5 — EMAIL DELIVERY (Issue 8: after Deep Dive, before Experiences) */}
          <section className={`py-14 md:py-16 border-b border-white/5 transition-all duration-700 ${revealStep >= 7 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="max-w-md">
              <p className="overline mb-3">Keep a copy of your Soulprint</p>
              <h3 className="font-heading text-xl font-light text-white mb-3">Your Soulprint report delivered to your inbox.</h3>
              <p className="text-[#71717A] text-sm leading-relaxed mb-6">
                {localStorage.getItem("travelorza_name") ? "If you provided an email during the discovery, your report was sent automatically. Enter below to receive it again." : "Enter your email and we'll send your full Soulprint report now."}
              </p>
              {emailDelivery.sent ? (
                <p className="text-sm" style={{ color: cfg.color }}>&#10003; Your Soulprint report is on its way.</p>
              ) : (
                <div className="flex gap-3">
                  <input type="email" placeholder="your@email.com" value={emailDelivery.email}
                    onChange={e => setEmailDelivery(s => ({ ...s, email: e.target.value }))}
                    className="flex-1 bg-transparent border-b border-white/12 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/22 py-2 text-sm transition-colors" />
                  <button onClick={async () => {
                      if (!emailDelivery.email.includes("@")) return;
                      setEmailDelivery(s => ({ ...s, loading: true }));
                      try {
                        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
                        const { default: axios } = await import("axios");
                        await axios.post(`${API}/leads`, {
                          name: localStorage.getItem("travelorza_name") || "Soulprint User",
                          phone: "", email: emailDelivery.email,
                          archetype: result.archetype, secondary_archetype: result.secondary_archetype || "",
                          scores: result.scores || {}, session_id: localStorage.getItem("travelorza_session") || "",
                          source: "soulprint_email_request"
                        });
                        setEmailDelivery(s => ({ ...s, sent: true, loading: false }));
                      } catch { setEmailDelivery(s => ({ ...s, loading: false })); }
                    }} disabled={emailDelivery.loading || !emailDelivery.email}
                    className="btn-gold px-5 py-2 text-xs disabled:opacity-40 whitespace-nowrap">
                    {emailDelivery.loading ? "Sending..." : "Send My Soulprint"}
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* SECTION 6 — JOURNEY DESIGN */}
          <section className={`py-20 md:py-24 border-b border-white/5 transition-all duration-700 ${revealStep >= 7 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-2xl p-8 md:p-14 border" style={{ background: `${cfg.color}04`, borderColor: `${cfg.color}14` }}>
              {leadSaved ? (
                <div data-testid="lead-saved-success" className="text-center py-8">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: `${cfg.color}10`, border: `1px solid ${cfg.color}28` }}>
                    <span style={{ color: cfg.color, fontSize: 22 }}>&#10003;</span>
                  </div>
                  <h4 className="font-heading text-2xl font-light text-white mb-2">Your journey has begun.</h4>
                  <p className="text-[#A1A1AA] text-sm">Your Experience Architect will be in touch within 24 hours.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                  <div>
                    <p className="overline mb-3" style={{ color: cfg.color }}>Your Next Step</p>
                    <h3 className="font-heading text-2xl md:text-3xl font-light text-white mb-4 tracking-tight leading-tight">
                      Design Your Journey<br /><span className="italic" style={{ color: cfg.color }}>as a {result.archetype}.</span>
                    </h3>
                    <p className="text-[#71717A] text-sm leading-relaxed">Share your details. Your Experience Architect will design a journey built around your Soulprint &mdash; specific, personal, and unlike anything you&apos;ve booked before.</p>
                  </div>
                  <div className="space-y-4">
                    {[{ key: "name", placeholder: "Your name", type: "text" },{ key: "email", placeholder: "your@email.com", type: "email" },{ key: "phone", placeholder: "+91 XXXXX XXXXX (optional)", type: "tel" }].map(f => (
                      <input key={f.key} data-testid={`lead-${f.key}-input`} type={f.type} placeholder={f.placeholder}
                        value={leadForm[f.key]} onChange={e => setLeadForm({ ...leadForm, [f.key]: e.target.value })}
                        className="w-full bg-transparent border-b border-white/12 focus:border-[#C9A84C] outline-none text-white placeholder:text-white/22 py-2.5 text-sm transition-colors" />
                    ))}
                    <button data-testid="quiz-lead-submit-btn" onClick={handleSaveLead} disabled={leadLoading || !leadForm.name || !leadForm.email}
                      className="w-full btn-gold py-4 text-xs disabled:opacity-40 mt-2 tracking-[0.22em] uppercase">
                      {leadLoading ? "Connecting..." : "Design My Journey"}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
              <button data-testid="quiz-result-design-btn" onClick={() => setModalOpen(true)} className="btn-gold text-xs px-10 py-3">Design My Journey</button>
              <button onClick={() => {
                  // Clear all persisted Soulprint data so the next visit starts fresh
                  ["travelorza_result", "travelorza_archetype", "travelorza_result_id",
                   "travelorza_secondary_archetype", "travelorza_dna_summary", "travelorza_name"].forEach(k => localStorage.removeItem(k));
                  setPhase("intro"); setAnswers([]); setResult(null);
                  setCurrentQ(0); setLeadSaved(false); setLeadForm({ name: "", email: "", phone: "" });
                  setRevealStep(0);
                }}
                className="text-[#71717A] text-xs uppercase tracking-[0.22em] hover:text-[#C9A84C] transition-colors">
                Retake My Soulprint
              </button>
            </div>
          </section>

          {/* SECTION 7 — MATCHING EXPERIENCES (moved to end) */}
          {result.matched_experiences?.length > 0 && (
            <section className={`py-20 md:py-24 transition-all duration-700 ${revealStep >= 8 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="overline mb-4">Experiences For Your Archetype</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-white mb-10 tracking-tight">
                Curated For<br /><span className="italic" style={{ color: cfg.color }}>Who You Are Right Now.</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {result.matched_experiences.map(exp => (
                  <ExpCard key={exp._id} exp={exp} archetypeColor={cfg.color} onInquire={e => { setModalExp(e); setModalOpen(true); }} />
                ))}
              </div>
              <div className="text-center mt-10">
                <a href={`/experiences?archetype=${encodeURIComponent(result.archetype)}`} className="btn-outline-gold text-xs px-8 py-3 inline-block">
                  Explore All {result.archetype} Experiences
                </a>
              </div>
            </section>
          )}

        </div>{/* end content area */}


        {/* Enquiry Modal */}
        <UniversalEnquiryModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          prefillExperience={modalExp?.title || ""}
          prefillArchetype={result?.archetype || ""}
          source="quiz_result"
        />

        {/* ORZA Quiz Companion — bottom sheet, triggers on 10s timer or share-card scroll */}
        <OrzaQuizCompanion result={result} sessionId={sessionId} externalTrigger={orzaExternalTrigger} />
      </div>
    );
  }

  // ── QUESTION SCREEN ───────────────────────────────────────────────────────────
  return (
    <div data-testid="quiz-question" className="min-h-screen bg-[#0A0A0A] flex flex-col">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-0.5 bg-white/5">
        <div className="h-full bg-[#C9A84C] transition-all duration-600 ease-out" style={{ width: `${progress}%` }} />
      </div>

      <div className={`flex-1 flex flex-col items-center justify-center px-6 py-16 max-w-3xl mx-auto w-full transition-all duration-300 ${isTransitioning ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
        {/* Header */}
        <div className="w-full flex items-center justify-between mb-12">
          <button data-testid="quiz-back-btn" onClick={handleBack} className="text-[#71717A] hover:text-[#C9A84C] transition-colors text-xs uppercase tracking-[0.2em] flex items-center gap-2">
            <ArrowLeft size={12} />Back
          </button>
          <div className="flex items-center gap-4">
            <span data-testid="quiz-category" className="text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] border border-[#C9A84C]/20 px-3 py-1 rounded-sm">
              {q.category}
            </span>
            <p data-testid="quiz-progress" className="text-[#71717A] text-xs uppercase tracking-[0.2em]">
              {String(currentQ + 1).padStart(2, "0")} / {String(QUIZ_QUESTIONS.length).padStart(2, "0")}
            </p>
          </div>
        </div>

        {/* Question */}
        <div className="w-full mb-4 text-center" key={currentQ}>
          <h2 data-testid="quiz-question-text" className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-white leading-snug tracking-tight mb-3">
            {q.question}
          </h2>
          {q.subtitle && (
            <p className="text-[#71717A] text-sm italic">{q.subtitle}</p>
          )}
        </div>

        {/* Options */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3 mt-10 mb-10" key={`opts-${currentQ}`}>
          {q.options.map((opt, i) => {
            const isSelected = selected === opt;
            // Get the dominant archetype for this option
            const dominantArch = Object.entries(opt.weights).sort((a, b) => b[1] - a[1])[0]?.[0];
            const optColor = dominantArch ? ARCHETYPE_CONFIG[dominantArch]?.color : "#C9A84C";
            return (
              <button
                key={`q${currentQ}-opt-${i}`}
                data-testid={`quiz-option-${i}`}
                onClick={() => handleSelect(opt)}
                className={`text-left p-5 rounded-xl border transition-all duration-200 group ${
                  isSelected
                    ? "border-[#C9A84C] bg-[#C9A84C]/8 text-white"
                    : "border-white/8 bg-[#111111] text-[#A1A1AA] hover:border-white/20 hover:text-white"
                }`}
                style={isSelected ? { borderColor: `${optColor}60`, background: `${optColor}08` } : {}}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${
                    isSelected ? "border-2" : "border border-white/20 group-hover:border-white/40"
                  }`} style={isSelected ? { borderColor: optColor, background: `${optColor}20` } : {}}>
                    {isSelected && <div className="w-2 h-2 rounded-full" style={{ background: optColor }} />}
                  </div>
                  <span className="text-sm leading-relaxed font-body">{opt.text}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Next button */}
        <button
          data-testid="quiz-next-btn"
          onClick={handleNext}
          disabled={!selected || isTransitioning}
          className="btn-gold text-xs px-14 py-4 disabled:opacity-25 disabled:cursor-not-allowed flex items-center gap-3 transition-all duration-200"
        >
          {currentQ === QUIZ_QUESTIONS.length - 1 ? "Reveal My Soulprint" : "Continue"}
          <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
}
